/**
 * mobileAdvancedPanel.ts — renders the shared CONTROL_SECTIONS into the
 * mobile drawer using mobile-styled inputs (big touch targets, dark
 * theme, accessible labels).
 *
 * Each section is a `<details>` group so users can collapse what they
 * don't need. State is in-memory only — drawer remembers nothing across
 * launches for these (low-traffic) controls.
 *
 * Disclosure: by default we render only `core`-tier sections + controls.
 * A "Show advanced" toggle at the top expands to advanced + lab tiers
 * so the surface still has the full registry available without dumping
 * the 27-slider wall on first open.
 */

import type { Renderer } from "../viz/renderer";
import {
  ControlSection,
  ControlTier,
  SelectDef,
  SliderDef,
  ToggleDef,
  defaultsFor,
  filterBySceneProfile,
  filterByTier,
  type SceneControlContext,
} from "./advancedControls";
import { haptic } from "./haptics";

/** Refresh handle for an individual control. */
interface ControlHandle {
  refresh: () => void;
}

/**
 * Build the entire advanced-controls UI inside `host`. Returns a refresh
 * function that re-syncs every input with current renderer params (call
 * this after a scene preset apply so sliders reflect the new values).
 */
export function buildMobileAdvancedPanel(
  host: HTMLElement,
  renderer: Renderer,
  getSceneContext?: () => SceneControlContext,
): { refresh: () => void } {
  host.innerHTML = "";
  const handles: ControlHandle[] = [];

  // -------- Disclosure toggle --------
  // Persists via localStorage so users who unlock advanced once don't
  // have to re-toggle every visit.
  const STORAGE_KEY = "pulsefx-mobile-adv-tier";
  const initialTier: ControlTier =
    (localStorage.getItem(STORAGE_KEY) as ControlTier | null) === "lab"
      ? "lab"
      : (localStorage.getItem(STORAGE_KEY) as ControlTier | null) === "advanced"
        ? "advanced"
        : "core";
  let currentTier: ControlTier = initialTier;

  const tierBar = document.createElement("div");
  tierBar.className = "mb-advTierBar";
  tierBar.style.cssText =
    "display:flex;gap:6px;padding:6px 0 10px 0;flex-wrap:wrap;";
  const tierLabel = document.createElement("span");
  tierLabel.textContent = "Disclosure:";
  tierLabel.style.cssText =
    "font:600 11px/1 system-ui,sans-serif;letter-spacing:.06em;text-transform:uppercase;opacity:.55;align-self:center;margin-right:4px;";
  tierBar.appendChild(tierLabel);
  const tierBtns: HTMLButtonElement[] = [];
  const makeTierBtn = (label: string, value: ControlTier): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.dataset.tier = value;
    b.textContent = label;
    b.style.cssText =
      "padding:6px 12px;border-radius:999px;font:600 11px/1 system-ui,sans-serif;" +
      "border:1px solid rgba(255,255,255,.18);background:rgba(255,255,255,.06);" +
      "color:rgba(255,255,255,.78);cursor:pointer;";
    b.addEventListener("click", () => {
      currentTier = value;
      localStorage.setItem(STORAGE_KEY, value);
      renderSections();
      paintTierBtns();
      haptic("light");
    });
    return b;
  };
  tierBtns.push(makeTierBtn("Core", "core"));
  tierBtns.push(makeTierBtn("Advanced", "advanced"));
  tierBtns.push(makeTierBtn("Lab", "lab"));
  tierBtns.forEach((b) => tierBar.appendChild(b));
  host.appendChild(tierBar);

  const paintTierBtns = (): void => {
    for (const b of tierBtns) {
      const active = b.dataset.tier === currentTier;
      b.style.background = active
        ? "rgba(120,180,255,.32)"
        : "rgba(255,255,255,.06)";
      b.style.color = active ? "#dff" : "rgba(255,255,255,.78)";
      b.style.borderColor = active
        ? "rgba(160,210,255,.85)"
        : "rgba(255,255,255,.18)";
    }
  };
  paintTierBtns();

  // Sections live in a sibling container so we can re-render in place
  // when the disclosure tier changes without losing the tier bar.
  const sectionsHost = document.createElement("div");
  sectionsHost.className = "mb-advSections";
  host.appendChild(sectionsHost);

  const renderSections = (): void => {
    sectionsHost.innerHTML = "";
    handles.length = 0;
    const sections = getSceneContext
      ? filterBySceneProfile(getSceneContext(), currentTier)
      : filterByTier(currentTier);
    for (const section of sections) {
      const group = renderSection(section, renderer, handles);
      sectionsHost.appendChild(group);
    }
  };
  renderSections();

  return {
    refresh(): void {
      renderSections();
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function renderSection(
  section: ControlSection,
  renderer: Renderer,
  handles: ControlHandle[],
): HTMLDetailsElement {
  const details = document.createElement("details");
  details.className = "mb-advGroup";

  const summary = document.createElement("summary");
  // Section title + an inline "Reset" affordance. Stops propagation so
  // tapping reset doesn't toggle the <details> open/closed.
  const title = document.createElement("span");
  title.textContent = section.title;
  summary.appendChild(title);
  if (section.tier && section.tier !== "core") {
    const badge = document.createElement("span");
    badge.textContent = section.tier === "lab" ? "LAB" : "ADV";
    badge.style.cssText =
      "margin-left:6px;padding:1px 6px;border-radius:6px;font:600 9px/1 system-ui,sans-serif;" +
      "background:rgba(255,255,255,.1);opacity:.7;letter-spacing:.06em;";
    summary.appendChild(badge);
  }
  const reset = document.createElement("button");
  reset.type = "button";
  reset.textContent = "Reset";
  reset.title = `Reset all ${section.title} controls to defaults`;
  reset.style.cssText =
    "margin-left:auto;padding:3px 8px;border-radius:6px;font:600 10px/1 system-ui,sans-serif;" +
    "border:1px solid rgba(255,255,255,.18);background:transparent;color:rgba(255,255,255,.7);" +
    "cursor:pointer;";
  reset.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const params = renderer.params as unknown as Record<string, unknown>;
    const resets = defaultsFor(section);
    for (const [k, v] of Object.entries(resets)) params[k] = v;
    handles.forEach((h) => h.refresh());
    haptic("medium");
  });
  // <summary> defaults to flex-less rendering — give it flex so the
  // Reset button can sit on the right.
  summary.style.display = "flex";
  summary.style.alignItems = "center";
  summary.appendChild(reset);
  details.appendChild(summary);

  const body = document.createElement("div");
  body.className = "mb-advGroup__body";

  if (section.hint) {
    const hint = document.createElement("p");
    hint.className = "mb-advGroup__hint";
    hint.textContent = section.hint;
    body.appendChild(hint);
  }

  for (const ctrl of section.controls) {
    if (ctrl.kind === "slider") {
      handles.push(renderSlider(body, ctrl, renderer));
    } else if (ctrl.kind === "select") {
      handles.push(renderSelect(body, ctrl, renderer));
    } else {
      handles.push(renderToggle(body, ctrl, renderer));
    }
  }
  details.appendChild(body);
  // Haptic tick on collapse/expand.
  details.addEventListener("toggle", () => haptic("tick"));
  return details;
}

function renderSlider(
  parent: HTMLElement,
  def: SliderDef,
  renderer: Renderer,
): ControlHandle {
  const row = document.createElement("div");
  row.className = "mb-advRow";
  row.title = def.tooltip;

  const top = document.createElement("div");
  top.className = "mb-advRow__top";
  const label = document.createElement("span");
  label.className = "mb-advRow__label";
  label.innerHTML = `${def.label}<span class="mb-advRow__sub">${def.sub}</span>`;
  const val = document.createElement("span");
  val.className = "mb-advRow__val";
  top.append(label, val);

  const input = document.createElement("input");
  input.type = "range";
  input.className = "mb-advRow__range";
  input.min = String(def.min);
  input.max = String(def.max);
  input.step = String(def.step);

  const ends = document.createElement("div");
  ends.className = "mb-advRow__ends";
  const left = document.createElement("span");
  left.textContent = def.leftHint;
  const right = document.createElement("span");
  right.textContent = def.rightHint;
  ends.append(left, right);

  const fmt = (v: number): string => {
    if (Math.abs(v) < 1) return v.toFixed(3);
    if (Math.abs(v) < 100) return v.toFixed(2);
    return Math.round(v).toString();
  };

  const refresh = (): void => {
    const v = Number((renderer.params as unknown as Record<string, unknown>)[
      def.key as string
    ]);
    input.value = String(v);
    val.textContent = fmt(v);
  };

  input.addEventListener("input", () => {
    const v = Number(input.value);
    (renderer.params as unknown as Record<string, unknown>)[def.key as string] = v;
    val.textContent = fmt(v);
  });

  row.append(top, input, ends);
  parent.appendChild(row);
  refresh();
  return { refresh };
}

function renderToggle(
  parent: HTMLElement,
  def: ToggleDef,
  renderer: Renderer,
): ControlHandle {
  const row = document.createElement("label");
  row.className = "mb-toggle mb-toggle--row mb-advToggle";
  row.title = def.tooltip;

  const input = document.createElement("input");
  input.type = "checkbox";
  const span = document.createElement("span");
  span.innerHTML = `${def.label} <em>${def.sub}</em>`;
  row.append(input, span);

  const refresh = (): void => {
    input.checked = Boolean(
      (renderer.params as unknown as Record<string, unknown>)[def.key as string],
    );
  };

  input.addEventListener("change", () => {
    (renderer.params as unknown as Record<string, unknown>)[def.key as string] =
      input.checked;
    haptic("tick");
  });

  refresh();
  parent.appendChild(row);
  return { refresh };
}

/**
 * Segmented-control style picker for enum-like params (Quality tier, etc.).
 * Renders as a row of buttons; tapped option is highlighted.
 */
function renderSelect(
  parent: HTMLElement,
  def: SelectDef,
  renderer: import("../viz/renderer").Renderer,
): ControlHandle {
  const row = document.createElement("div");
  row.className = "mb-advRow";
  row.title = def.tooltip;

  const top = document.createElement("div");
  top.className = "mb-advRow__top";
  const label = document.createElement("span");
  label.className = "mb-advRow__label";
  label.innerHTML = `${def.label}<span class="mb-advRow__sub">${def.sub}</span>`;
  top.appendChild(label);

  const seg = document.createElement("div");
  seg.className = "mb-advSeg";
  const buttons: HTMLButtonElement[] = [];
  for (const opt of def.options) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "mb-advSegBtn";
    b.textContent = opt.label;
    b.dataset.value = opt.value;
    b.addEventListener("click", () => {
      (renderer.params as unknown as Record<string, unknown>)[def.key as string] =
        opt.value;
      haptic("medium");
      refresh();
    });
    seg.appendChild(b);
    buttons.push(b);
  }

  const refresh = (): void => {
    const cur = String(
      (renderer.params as unknown as Record<string, unknown>)[def.key as string],
    );
    for (const b of buttons) {
      b.classList.toggle("mb-advSegBtn--active", b.dataset.value === cur);
    }
  };

  row.append(top, seg);
  parent.appendChild(row);
  refresh();
  return { refresh };
}
